import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getCustomers } from '../services/api';
import './CustomerSelector.css';

function CustomerSelector({ onSelect, selectedCustomer, onCreateNew }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (selectedCustomer) {
      setSearch(selectedCustomer.name);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showDropdown || !search) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await getCustomers(search);
        setResults(response.customers || []);
      } catch (error) {
        console.error('Error searching customers:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search, showDropdown]);

  const handleFocus = () => {
    setShowDropdown(true);
  };

  const handleSelect = (customer) => {
    onSelect(customer);
    setSearch(customer.name);
    setShowDropdown(false);
  };

  const handleCreate = () => {
    onCreateNew();
    setShowDropdown(false);
  };

  const handleClear = () => {
    onSelect(null);
    setSearch('');
  };

  return (
    <div className="customer-selector" ref={wrapperRef}>
      <div className="input-wrapper">
        <input
          type="text"
          placeholder={t('customers.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={handleFocus}
          className="customer-input"
        />
        {selectedCustomer && (
          <button
            type="button"
            className="clear-btn"
            onClick={handleClear}
            title={t('common.clear')}
          >
            &times;
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="customer-dropdown">
          {loading ? (
            <div className="dropdown-item loading">{t('common.loading')}</div>
          ) : (
            <>
              {results.length > 0
                ? results.map((customer) => (
                    <div
                      key={customer.customerId}
                      className="dropdown-item"
                      onClick={() => handleSelect(customer)}
                    >
                      <div className="customer-name">{customer.name}</div>
                      <div className="customer-phone">{customer.phone}</div>
                    </div>
                  ))
                : search.length > 1 && (
                    <div className="dropdown-item no-results">{t('customers.noResults')}</div>
                  )}
              <div className="dropdown-divider"></div>
              <div className="dropdown-item create-new" onClick={handleCreate}>
                + {t('customers.add')}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomerSelector;
